import { useEffect } from 'react';
import {
  MapContainer as LeafletMapContainer,
  TileLayer,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import {
  DEFAULT_ZOOM,
  DARK_TILES,
  LIGHT_TILES,
  MAX_ZOOM,
  MEXICO_CENTER,
  TILE_ATTRIBUTION,
} from '@/utils/constants';
import { useUIStore } from '@/stores/uiStore';
import VehicleMarkers from './VehicleMarkers';
import RoutePolylines from './RoutePolylines';
import RiskZones from './RiskZones';
import POIMarkers from './POIMarkers';
import SearchArea from './SearchArea';
import HighlightPulse from './HighlightPulse';
import MapLoadingOverlay from './MapLoadingOverlay';
import MapControls from './MapControls';
import FloatingStats from './FloatingStats';
import RouteMapLayer from './RouteMapLayer';
import VehicleSearch from './VehicleSearch';

function TileUpdater() {
  const map = useMap();
  const theme = useUIStore((s) => s.theme);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const sidebarWidth = useUIStore((s) => s.sidebarWidth);
  const fleetPanelOpen = useUIStore((s) => s.fleetPanelOpen);

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => map.invalidateSize(), 100);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [map]);

  // Invalidate map size when sidebar opens/closes or resizes
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 50);
    return () => clearTimeout(timer);
  }, [map, sidebarOpen, sidebarWidth]);

  // Invalidate map size after fleet panel split animation completes (500ms transition)
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 550);
    return () => clearTimeout(timer);
  }, [map, fleetPanelOpen]);

  // Listen for fleet panel "Ver en mapa" click → flyTo
  useEffect(() => {
    const handler = (e: Event) => {
      const { lat, lon, zoom } = (e as CustomEvent).detail ?? {};
      if (lat != null && lon != null) {
        map.flyTo([lat, lon], zoom ?? 14, { animate: true, duration: 1.2 });
      }
    };
    window.addEventListener('leaflet-center', handler);
    return () => window.removeEventListener('leaflet-center', handler);
  }, [map]);

  const tileUrl = theme === 'dark' ? DARK_TILES : LIGHT_TILES;

  return (
    <TileLayer
      key={theme}
      url={tileUrl}
      attribution={TILE_ATTRIBUTION}
      className={theme === 'aurora' ? 'aurora-tiles' : ''}
    />
  );
}

export default function MapContainerComponent() {
  return (
    <div className="flex-1 relative">
      <LeafletMapContainer
        center={MEXICO_CENTER}
        zoom={DEFAULT_ZOOM}
        maxZoom={MAX_ZOOM}
        zoomControl={false}
        preferCanvas={true}
        className="w-full h-full"
      >
        <TileUpdater />
        <VehicleMarkers />
        <RoutePolylines />
        <RiskZones />
        <POIMarkers />
        <SearchArea />
        <HighlightPulse />
        <RouteMapLayer />
        <MapControls />
      </LeafletMapContainer>
      <MapLoadingOverlay />
      <FloatingStats />
      <div className="absolute top-5 left-5 z-[800]">
        <VehicleSearch />
      </div>
    </div>
  );
}
